<template>
  <SoPageWithHeader title="Settings">
    <SoContainer>
      <SoWidget
        title="Your profile"
        class="mb-5"
        :loading="userProfile.isFetching"
      >
        <h1 class="mb-5">Welcome {{ userProfile.data.givenName }}!</h1>
        <h5 class="mb-2"><strong>Profile details</strong></h5>
        <dl class="mb-5 dl w-25">
          <dt>Full Name</dt>
          <dd>{{ userProfile.data.displayName }}</dd>
          <dt>Email</dt>
          <dd>{{ userProfile.data.email }}</dd>
          <template v-if="userProfile.data.jobTitle">
            <dt>Job Title</dt>
            <dd>{{ userProfile.data.jobTitle }}</dd>
          </template>
          <dt>Roles</dt>
          <dd>{{ userRoleList }}</dd>
        </dl>
        <SoButton title="Logout" icon="logout" @click="logout"></SoButton>
      </SoWidget>

      <SoWidget v-if="isDeveloper" class="mb-5">
        <h6 class="d-flex align-center font-weight-medium">
          Role selector
          <SoTooltip text="Use this to test different permissions in Nova">
            <SoIcon icon="info sm" color="info" class="ml-1" />
          </SoTooltip>
          <v-spacer />
          <SoToggle v-model="mockPermissions.enabled" />
        </h6>
        <template #expand>
          <div v-show="mockPermissions.enabled" class="mx-4">
            <SoSelect
              v-model="mockPermissions.roles"
              :items="Object.values(ROLES)"
              :disabled="!mockPermissions.enabled"
              placeholder="None"
              clearable
              type="multiple"
              class="pb-4"
            />
          </div>
        </template>
      </SoWidget>

      <SoWidget title="Theme selector">
        <SoRadioGroup
          v-model="selectedTheme"
          :items="
            currentTheme.themeNames.map((theme) => ({
              text: formatThemeTitle(theme.title),
              value: theme.title,
            }))
          "
          panel
        />
      </SoWidget>
    </SoContainer>
  </SoPageWithHeader>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { AuthService } from "@/services/AuthService"
  import { ProfileService } from "@/services/ProfileService"
  import { useMockPermissionsStore } from "@/stores/mockPermissions"
  import { ROLES } from "@/services/PermissionsService"
  import SoRadioGroup from "@/components/formControls/SoRadioGroup.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import SoPageWithHeader from "@/components/templates/SoPageWithHeader.vue"
  import SoContainer from "@/components/SoContainer.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoIcon from "@/components/SoIcon.vue"
  import SoToggle from "@/components/formControls/SoToggle.vue"

  import { useThemeHelper } from "@/theme/themeHelper"
  import { capitalize } from "@/helpers/stringHelpers"
  import { themeOptions } from "@/constants/themeOptions"

  export default defineComponent({
    components: {
      SoWidget,
      SoPageWithHeader,
      SoRadioGroup,
      SoButton,
      SoContainer,
      SoSelect,
      SoTooltip,
      SoIcon,
      SoToggle,
    },
    data() {
      return {
        selectedTheme: null as null | string,
        currentTheme: useThemeHelper(),
        mockPermissions: useMockPermissionsStore(),
        ROLES,
      }
    },
    computed: {
      userProfile: () => ProfileService.requestUserProfile(),
      userRoles: () => AuthService.getUserRoles(),
      userRoleList() {
        return (
          this.userRoles.map((user) => capitalize(user)).join(", ") || "None"
        )
      },
      isDeveloper: () => AuthService.isDeveloper(),
    },
    watch: {
      selectedTheme(theme) {
        this.currentTheme.name = theme
        window.localStorage.setItem(themeOptions.LOCAL_STORAGE_KEY, theme)
      },
    },
    mounted() {
      this.selectedTheme =
        window.localStorage.getItem(themeOptions.LOCAL_STORAGE_KEY) ??
        this.currentTheme.name
    },

    methods: {
      logout() {
        AuthService.logout()
      },
      formatThemeTitle(theme: string) {
        return capitalize(theme.replace(/([a-z])([A-Z])/g, "$1 $2"))
      },
    },
  })
</script>
